import * as THREE from "three";
import { Assets } from "utils/imports.utils";
import { disposeMaterial } from "utils/functions.utils";
import { IObject, ObjectTypes } from "utils/interface.utils";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { cleanupTransformBox, renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";
import { applyTransformCursor } from "components/editor_workspace/scene/transform_cursor.helper";

export const createObjectVideo = (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, targetImageRef, objectsRef, selectedObjectIDRef, selectedTransformControlRef } = workspace.refs;
    const { src } = properties;

    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !loaderRef.current || !src) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const loader = loaderRef.current;
    const targetImageGroup = targetImageRef.current;

    try {
        const objectVideo = renderObjectVideo(workspace, properties, camera, scene, loader, renderer, objectID, targetImageGroup)

        objectsRef.current.set(
            objectID,
            {
                mesh: objectVideo.mesh,
                cleanup: objectVideo.cleanup
            }
        )

        // A newly added object is selected with the Position tool already active; the tool
        // effect ran before this mesh existed (textures load async), so attach its box here.
        if (objectID === selectedObjectIDRef.current && selectedTransformControlRef.current) {
            renderTransformBox(workspace, objectVideo.mesh);
        }

        if (rendererRef.current) {
            rendererRef.current.render(scene, camera);
        }

    } catch (error) {
        console.log("Error creating object video:", error);
    }
}

export const updateObjectVideo = (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, rotationSystemRef, scaleSystemRef, videoPlayerControlsDimensionsRef, objectsRef, allObjectsPreviousScaleRef } = workspace.refs;
    if (rotationSystemRef.current?.isObjectBeingDraggedAndRotated(objectID)) return;
    if (scaleSystemRef.current?.isObjectBeingDraggedAndScaled(objectID)) return;

    const { src, positionX, positionY, scaleX, scaleY, rotationX, renderOrder } = properties;

    const objectData = objectsRef.current.get(objectID)
    if (!src || !objectData || !objectData.mesh || !loaderRef.current) return;

    const mesh = objectData.mesh as THREE.Group & { userData: { controlButton?: { mesh: THREE.Mesh } } };

    if (positionX !== undefined && mesh.position.x !== positionX) mesh.position.x = positionX;
    if (positionY !== undefined && mesh.position.y !== positionY) mesh.position.y = positionY;

    if (mesh.userData.transformBox) {
        mesh.userData.transformBox.position.set(positionX || 0, positionY || 0, 0)
    }

    if (rotationX !== undefined) {
        const rotationXRadians = THREE.MathUtils.degToRad(rotationX);
        if (mesh.rotation.z !== rotationXRadians) {
            mesh.rotation.z = rotationXRadians;
        }

        if (mesh.userData.transformBox) {
            mesh.userData.transformBox.rotation.z = rotationXRadians;
        }
    }

    const getPreviousObjectScale = allObjectsPreviousScaleRef.current.find((object: { ID: string | null, scale: { x: number, y: number, z: number } }) => object.ID === objectID);

    if (getPreviousObjectScale?.scale) {
        let shouldUpdateTransformBox = false;
        if (scaleX !== undefined && scaleX !== getPreviousObjectScale?.scale?.x) {
            mesh.scale.x = Math.max(scaleX, 0.001)
            getPreviousObjectScale.scale.x = scaleX;
            shouldUpdateTransformBox = true;
        }

        if (scaleY !== undefined && scaleY !== getPreviousObjectScale?.scale?.y) {
            mesh.scale.y = Math.max(scaleY, 0.001)
            getPreviousObjectScale.scale.y = scaleY;
            shouldUpdateTransformBox = true;
        }

        if (shouldUpdateTransformBox && mesh.userData.transformBox) {
            renderTransformBox(workspace, mesh);
        }

        // The control button's local scale was set once at creation to counteract the
        // group's scale at that time (buttonWidth/scaleX, buttonHeight/scaleY) so its
        // effective size stays constant. That compensation goes stale as soon as the
        // group's scale changes here -- re-derive it against the new scale.
        if (shouldUpdateTransformBox && mesh.userData.controlButton?.mesh) {
            const buttonWidth = videoPlayerControlsDimensionsRef.current.width;
            const buttonHeight = videoPlayerControlsDimensionsRef.current.height;
            mesh.userData.controlButton.mesh.scale.set(buttonWidth / mesh.scale.x, buttonHeight / mesh.scale.y, 1);
        }
    }

    // Update the render order
    if (renderOrder !== undefined) {

        // Set renderOrder on the group
        mesh.renderOrder = renderOrder;
        mesh.userData.renderOrder = renderOrder;

        mesh.children.forEach(child => {
            if (child instanceof THREE.Mesh) {
                child.renderOrder = renderOrder;
                if (child.userData) child.userData.renderOrder = renderOrder;
            }
        });

        // Force renderer update
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    }

    const currentSrc = mesh.userData.src;
    if (currentSrc !== src) {
        const group = mesh as THREE.Group;
        const videoMesh = group.children.find(child =>
            child instanceof THREE.Mesh &&
            child.material instanceof THREE.MeshBasicMaterial &&
            child.material.map instanceof THREE.VideoTexture) as THREE.Mesh;

        if (videoMesh && videoMesh.material instanceof THREE.MeshBasicMaterial) {
            const material = videoMesh.material as THREE.MeshBasicMaterial;
            const oldTexture = material.map as THREE.VideoTexture;

            if (oldTexture) {
                // No need to dispose, just update the video element source
                const videoElement = oldTexture.image as HTMLVideoElement;
                videoElement.src = src;
                videoElement.load(); // Reset and load new video
            }

            mesh.userData.src = src;
        }
    }
}

export const renderObjectVideo = (workspace: WorkspaceContext, properties: IObject, camera: THREE.OrthographicCamera, scene: THREE.Scene, loader: THREE.TextureLoader, renderer: THREE.WebGLRenderer, objectID: string, targetImageGroup: THREE.Group | null) => {
    const { videoPlayerControlsDimensionsRef, allObjectsPreviousScaleRef } = workspace.refs;
    const { src, positionX, positionY, scaleX, scaleY, renderOrder } = properties;
    const parentObject = targetImageGroup || scene;

    const videoElement = document.createElement("video");
    videoElement.src = src;
    videoElement.crossOrigin = "Anonymous";
    videoElement.muted = true;
    videoElement.playsInline = true;

    const videoTexture = new THREE.VideoTexture(videoElement);
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.format = THREE.RGBAFormat;

    const objectMaterial = new THREE.MeshBasicMaterial({ map: videoTexture, transparent: true, opacity: 1.0, depthWrite: true, depthTest: true });
    const objectGeometry = new THREE.PlaneGeometry(1, 1);
    const objectVideo = new THREE.Mesh(objectGeometry, objectMaterial);

    allObjectsPreviousScaleRef.current.push({
        ID: objectID,
        scale: {
            x: scaleX,
            y: scaleY,
            z: 1
        }
    })

    const videoGroup = new THREE.Group();
    videoGroup.add(objectVideo);
    videoGroup.position.set(positionX, positionY, 0);
    videoGroup.scale.set(scaleX, scaleY, 1);

    const existingChild = parentObject.children.find(child => child.userData.id === objectID);
    if (existingChild) {
        parentObject.remove(existingChild);
    }
    parentObject.add(videoGroup);

    videoGroup.userData.id = objectID;
    videoGroup.userData.src = src;
    videoGroup.userData.isDraggable = true;
    videoGroup.userData.objectType = ObjectTypes.VIDEO;
    videoGroup.userData.videoElement = videoElement;

    videoGroup.userData.originalWidth = scaleX;
    videoGroup.userData.originalHeight = scaleY;

    let isPlaying = false;

    const createControlButton = () => {
        const playTexture = loader.load(Assets.video_play);
        playTexture.minFilter = THREE.LinearMipmapLinearFilter;
        playTexture.magFilter = THREE.LinearFilter;
        playTexture.generateMipmaps = true;

        const pauseTexture = loader.load(Assets.video_pause);
        pauseTexture.minFilter = THREE.LinearMipmapLinearFilter;
        pauseTexture.magFilter = THREE.LinearFilter;
        pauseTexture.generateMipmaps = true;

        const buttonMaterial = new THREE.MeshBasicMaterial({
            map: playTexture,
            transparent: true,
            depthTest: false
        });

        const buttonWidth = videoPlayerControlsDimensionsRef.current.width;
        const buttonHeight = videoPlayerControlsDimensionsRef.current.height;

        const buttonGeometry = new THREE.PlaneGeometry(1, 1);
        const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial)

        buttonMesh.position.copy(objectVideo.position);
        buttonMesh.position.z = objectVideo.position.z + 0.01;
        buttonMesh.scale.set(buttonWidth / scaleX, buttonHeight / scaleY, 1);

        const updateButtonTexture = () => {
            buttonMaterial.map = isPlaying ? pauseTexture : playTexture;
            buttonMaterial.needsUpdate = true;
        };

        return {
            mesh: buttonMesh,
            updateTexture: updateButtonTexture,
            show: () => buttonMesh.visible = true,
            hide: () => buttonMesh.visible = false,
        }
    }

    const controlButton = createControlButton();
    videoGroup.add(controlButton.mesh);
    videoGroup.userData.controlButton = controlButton;

    videoGroup.renderOrder = renderOrder;
    videoGroup.children.forEach(child => {
        child.renderOrder = renderOrder;
        if (child.userData) child.userData.renderOrder = renderOrder;
    });

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const handleCanvasClick = (event: MouseEvent) => {
        const canvas = renderer.domElement;
        const rect = canvas.getBoundingClientRect();

        // Calculate mouse position in normalized device coordinates (-1 to +1)
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);

        const intersects = raycaster.intersectObject(controlButton.mesh);

        if (intersects.length > 0) {
            togglePlayback();
        }
    }

    let wasOverPlayButton = false;

    const handleMouseMove = (event: MouseEvent) => {
        const canvas = renderer.domElement;
        const rect = canvas.getBoundingClientRect();

        // Calculate mouse position in normalized device coordinates (-1 to +1)
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);

        const intersects = raycaster.intersectObject(controlButton.mesh);

        // Only touch the cursor when the pointer enters or leaves the play button. Writing on every
        // move would overwrite what the transform tools set per corner (e.g. Scale's diagonals).
        const isOverPlayButton = intersects.length > 0;
        if (isOverPlayButton === wasOverPlayButton) return;
        wasOverPlayButton = isOverPlayButton;

        if (isOverPlayButton) {
            renderer.domElement.style.cursor = 'pointer';
        } else {
            applyTransformCursor(renderer.domElement, { tool: workspace.refs.selectedTransformControlRef.current, corner: null, dragging: false });
        }
    }

    const togglePlayback = () => {
        const videoElement = videoGroup.userData.videoElement;

        if (!videoElement) return;

        if (!isPlaying) {
            videoElement.play().then(() => {
                isPlaying = true;
                controlButton.updateTexture();
                // Optional: hide button after play starts
                // setTimeout(() => controlButton.hide(), 1000);
            }).catch(error => {
                console.error("Video playback error:", error);
            });
        } else {
            videoElement.pause();
            isPlaying = false;
            controlButton.updateTexture();
            controlButton.show();
        }
    }

    // Add click event listener to canvas
    renderer.domElement.addEventListener('click', handleCanvasClick);
    renderer.domElement.addEventListener("mousemove", handleMouseMove);

    let animationFrameId: number;
    const animate = () => {
        animationFrameId = requestAnimationFrame(animate);
        if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
            videoTexture.needsUpdate = true;
        }
        renderer.render(scene, camera);
    };
    animate();

    return {
        mesh: videoGroup,
        cleanup: () => {
            // The transform box is a sibling of the object, so removing the object alone leaves it on the canvas.
            cleanupTransformBox(workspace, videoGroup);
            cancelAnimationFrame(animationFrameId);

            renderer.domElement.removeEventListener('click', handleCanvasClick);
            renderer.domElement.removeEventListener("mousemove", handleMouseMove);
            applyTransformCursor(renderer.domElement, { tool: workspace.refs.selectedTransformControlRef.current, corner: null, dragging: false });

            videoElement.pause();
            videoElement.removeAttribute('src');
            videoElement.load();

            videoTexture.dispose();

            if (controlButton.mesh.material instanceof THREE.MeshBasicMaterial &&
                controlButton.mesh.material.map) {
                controlButton.mesh.material.map.dispose();
            }

            videoGroup.traverse((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.geometry) object.geometry.dispose();

                    if (object.material) {
                        // Handle array of materials
                        if (Array.isArray(object.material)) {
                            object.material.forEach(material => disposeMaterial(material));
                        } else {
                            disposeMaterial(object.material);
                        }
                    }
                }
            });

            // Clear any user data references that might cause memory leaks
            if (videoGroup.userData) {
                // Clear specific references that might cause circular references
                if (videoGroup.userData.videoElement) videoGroup.userData.videoElement = null;
                if (videoGroup.userData.controlButton) videoGroup.userData.controlButton = null;
            }

            if (parentObject) {
                const childToRemove = parentObject.children.find(child => child.userData.id === objectID);
                if (childToRemove) {
                    parentObject.remove(childToRemove);
                }
            }
        }
    }
}
